import React, { useMemo } from "react";
import { Edges } from "@react-three/drei";
import { Shape, Path, ExtrudeGeometry, ShapeGeometry, Texture, DoubleSide, BackSide } from "three";
import { WindowSize, GridSize, GlasspanelPosition } from "../../../../../../models/door_configuration";
import { getSezioneGlasspanel } from "../../../../../../core/common_functions";
import { calcolaNumeroFinestreDaLarghezzaPorta } from "../../../builder/components/object-draw/draw_utils";
import {
  MM,
  FINESTRA_LARGHEZZA,
  FINESTRA_ALTEZZA,
  GRIGLIA_LARGHEZZA,
  GRIGLIA_ALTEZZA,
  GRIGLIA_400_LARGHEZZA,
  GRIGLIA_400_ALTEZZA,
  ALTEZZA_RAIL_M,
  RILIEVO_RAIL_M,
  C_VETRO,
  C_VETRO_BORDO,
  C_AREA_SELEZIONABILE,
  VETRO_SATINATO_MATERIAL,
  VETRO_TRASPARENTE_MATERIAL,
  OverbasStep,
  IntegratedDoorPosition,
} from "./constants";
import { Finestra } from "./finestra";
import { Griglia } from "./griglia";
import { ZonaCliccabile } from "./zona_cliccabile";
import { slotOverlapsManiglia } from "./maniglia_basculante";
import { allineaSlotAllaPortina } from "./portina_integrata";
import { PannelloSuperficieAerata } from "./superficie_areata";
import { texturaTile } from "./texture_tile";

const TELAIO_FV = MM(40);

export type PannelloSingoloProps = {
  larghezza: number;
  altezza: number;
  spessore: number;
  panelIndex: number;
  panelWorldY: number;
  showPortina: boolean;
  portinaX: number;
  portinaLarghezza: number;
  step: OverbasStep;
  finestreAttive: Set<string>;
  griglieAttive: Set<string>;
  onFinestraClick: (panelIndex: number, finestraIndex: number) => void;
  isDoorClosed: boolean;
  colorePannello: Texture | undefined;
  coloreInternoPannello: Texture | undefined;
  corniceFinestratura: Texture | undefined;
  isWide: boolean;
  isFullVision: boolean;
  /** Larghezza di ogni campata verticale del pannello, da sinistra a destra. */
  campate: number[];
  /** Vetratura satinata scelta nello step Aspetto: le lastre diventano lattiginose. */
  vetroSatinato: boolean;
  windowSize: WindowSize;
  glasspanelLimitReached: boolean;
  /** Altezza della sezione glasspanel (m) e posizione nel pannello; senza altezza la vetrata riempie il pannello. */
  glasspanelAltezza?: number;
  glasspanelPosizione?: GlasspanelPosition;
  hoverColor: string;
  gridSize: GridSize;
  grigliaColor: string | undefined;
  tipoManiglia?: string | null;
  manigliaLockY?: number;
  manigliaSquareY?: number;
};

export const PannelloSingolo: React.FC<PannelloSingoloProps> = ({
  larghezza,
  altezza,
  spessore,
  panelIndex,
  panelWorldY,
  showPortina,
  portinaX,
  portinaLarghezza,
  step,
  finestreAttive,
  griglieAttive,
  onFinestraClick,
  isDoorClosed,
  colorePannello,
  coloreInternoPannello,
  corniceFinestratura,
  isWide,
  isFullVision,
  campate,
  vetroSatinato,
  windowSize,
  glasspanelLimitReached,
  glasspanelAltezza,
  glasspanelPosizione,
  hoverColor,
  gridSize,
  grigliaColor,
  tipoManiglia,
  manigliaLockY,
  manigliaSquareY,
}) => {
  // Texture mappata in coordinate reali: il campione non viene stirato sul pannello e,
  // grazie allo scostamento verticale, la venatura prosegue da un pannello all'altro
  // invece di ripartire identica su ognuno.
  const baseY              = panelWorldY - altezza / 2;   // spigolo basso del pannello sulla porta
  const texPannello        = texturaTile(colorePannello, larghezza, altezza, 0, baseY);
  const texPannelloInterno = texturaTile(coloreInternoPannello ?? colorePannello, larghezza, altezza, 0, baseY);
  const texRailTop         = texturaTile(colorePannello, larghezza, ALTEZZA_RAIL_M, 0, baseY + altezza - ALTEZZA_RAIL_M);
  const texRailBot         = texturaTile(colorePannello, larghezza, ALTEZZA_RAIL_M, 0, baseY);

  const isFinestre      = step === "finestratura";
  const isVentilazione  = step === "ventilazione";
  const isSuperficieAreataType = gridSize === GridSize.superificie_fq || gridSize === GridSize.superificie_ft;
  const isSuperficieAreata = isVentilazione && isSuperficieAreataType;
  const isPanelSuperficieAttiva = isSuperficieAreataType && griglieAttive.has(`${panelIndex}-0`);
  const panelHasFinestre = isSuperficieAreata &&
    Array.from(finestreAttive).some(k => k.startsWith(`${panelIndex}-`));

  const isGlassWindowType = (
    windowSize === WindowSize.glasspanel ||
    windowSize === WindowSize.glasspanel_vetro_6_7 ||
    windowSize === WindowSize.glasspanel_vetro_5 ||
    windowSize === WindowSize.glasspanel_vetro_4_12_4
  );
  const isGlasspanel = isFinestre && isGlassWindowType;

  // Sezione glasspanel della PLANA: la vetrata ha l'altezza scelta e si posiziona da sola nel
  // pannello. Senza altezza (vetrata integrale, classic) riempie tutto il pannello.
  const isSezioneParziale = isGlassWindowType && !!glasspanelAltezza && glasspanelAltezza < altezza;
  const sezioneVetro = isSezioneParziale
    ? getSezioneGlasspanel(altezza, glasspanelAltezza!, glasspanelPosizione)
    : { altezza, dalBasso: 0 };
  const sezioneH  = sezioneVetro.altezza;
  const sezioneCy = -altezza / 2 + sezioneVetro.dalBasso + sezioneH / 2;

  const finestraW = windowSize === WindowSize._230x230 ? MM(230) : MM(540);
  const finestraH = windowSize === WindowSize._230x230 ? MM(230) : FINESTRA_ALTEZZA;
  const gridSlotW = gridSize === GridSize._400x200 ? GRIGLIA_400_LARGHEZZA : GRIGLIA_LARGHEZZA;
  const gridSlotH = gridSize === GridSize._400x200 ? GRIGLIA_400_ALTEZZA : GRIGLIA_ALTEZZA;

  // Per WIDE la finestra/griglia va al livello Z delle sezioni rialzate
  const slotZ = isWide ? RILIEVO_RAIL_M : 0;

  const windowPositions = useMemo(() => {
    const n = calcolaNumeroFinestreDaLarghezzaPorta(larghezza * 1000);
    if (n <= 0) return [] as [number, number, number][];
    const gap = (larghezza - n * finestraW) / (n + 1);
    const positions: [number, number, number][] = [];
    for (let i = 0; i < n; i++) {
      const x = -larghezza / 2 + gap + finestraW / 2 + i * (finestraW + gap);
      positions.push([x, 0, slotZ]);
    }
    return positions;
  }, [larghezza, finestraW, slotZ]);

  const grigliaPositions = useMemo(() => {
    const n = calcolaNumeroFinestreDaLarghezzaPorta(larghezza * 1000);
    if (n <= 0) return [] as [number, number, number][];
    const gap = (larghezza - n * gridSlotW) / (n + 1);
    const positions: [number, number, number][] = [];
    for (let i = 0; i < n; i++) {
      const x = -larghezza / 2 + gap + gridSlotW / 2 + i * (gridSlotW + gap);
      positions.push([x, 0, slotZ]);
    }
    return positions;
  }, [larghezza, gridSlotW, slotZ]);

  // La griglia a passo fisso non conosce la portina: gli slot che la intersecano vengono
  // ricentrati sul vano (uno solo, quello che si sposta di meno) o bloccati.
  const finestraSlotX = useMemo(() => {
    const xs = windowPositions.map(p => p[0]);
    if (!showPortina) return xs as (number | null)[];
    return allineaSlotAllaPortina(xs, panelWorldY, portinaX, portinaLarghezza, finestraW, finestraH);
  }, [windowPositions, showPortina, panelWorldY, portinaX, portinaLarghezza, finestraW, finestraH]);

  const grigliaSlotX = useMemo(() => {
    const xs = grigliaPositions.map(p => p[0]);
    if (!showPortina) return xs as (number | null)[];
    return allineaSlotAllaPortina(xs, panelWorldY, portinaX, portinaLarghezza, gridSlotW, gridSlotH);
  }, [grigliaPositions, showPortina, panelWorldY, portinaX, portinaLarghezza, gridSlotW, gridSlotH]);

  // Centro e larghezza di ogni campata: le campate si affiancano da sinistra a destra e quello
  // che avanza sulla larghezza del pannello e' lo spazio dei montanti che le separano.
  const campateSegments = useMemo((): [number, number][] => {
    const totale = campate.reduce((somma, w) => somma + w, 0);
    const montante = campate.length > 1 ? (larghezza - totale) / (campate.length - 1) : 0;
    let x = -larghezza / 2;

    return campate.map(w => {
      const segmento: [number, number] = [x + w / 2, w];
      x += w + montante;

      return segmento;
    });
  }, [campate, larghezza]);

  // Segmenti X dove renderizzare le sezioni rialzate WIDE (esclusa l'area delle finestre attive)
  const wideSectionSegments = useMemo((): [number, number][] => {
    if (!isWide) return [];
    const activeRanges = finestraSlotX
      .map((x, i) => x !== null && finestreAttive.has(`${panelIndex}-${i}`) ? [x - finestraW / 2, x + finestraW / 2] as [number, number] : null)
      .filter((r): r is [number, number] => r !== null);
    if (activeRanges.length === 0) return [[0, larghezza]];
    activeRanges.sort((a, b) => a[0] - b[0]);
    const segs: [number, number][] = [];
    const leftW = activeRanges[0][0] - (-larghezza / 2);
    if (leftW > MM(1)) segs.push([-larghezza / 2 + leftW / 2, leftW]);
    for (let i = 0; i < activeRanges.length - 1; i++) {
      const gapStart = activeRanges[i][1];
      const gapEnd   = activeRanges[i + 1][0];
      const gapW     = gapEnd - gapStart;
      if (gapW > MM(1)) segs.push([gapStart + gapW / 2, gapW]);
    }
    const rightW = larghezza / 2 - activeRanges[activeRanges.length - 1][1];
    if (rightW > MM(1)) segs.push([larghezza / 2 - rightW / 2, rightW]);
    return segs;
  }, [isWide, finestraSlotX, finestreAttive, panelIndex, finestraW, larghezza]);

  const panelShape = useMemo(() => {
    const shape = new Shape();
    shape.moveTo(-larghezza / 2, -altezza / 2);
    shape.lineTo( larghezza / 2, -altezza / 2);
    shape.lineTo( larghezza / 2,  altezza / 2);
    shape.lineTo(-larghezza / 2,  altezza / 2);
    shape.lineTo(-larghezza / 2, -altezza / 2);

    if (isFullVision) {
      const hw = larghezza / 2 - TELAIO_FV;
      const hh = altezza / 2 - TELAIO_FV;
      const hole = new Path();
      hole.moveTo(-hw, -hh);
      hole.lineTo( hw, -hh);
      hole.lineTo( hw,  hh);
      hole.lineTo(-hw,  hh);
      hole.lineTo(-hw, -hh);
      shape.holes.push(hole);
    } else if (isGlassWindowType) {
      if (finestreAttive.has(`${panelIndex}-0`)) {
        const BORDO_GP = MM(30);
        const hh = sezioneH / 2 - BORDO_GP;
        campateSegments.forEach(([cx, w]) => {
          const hw = w / 2 - BORDO_GP;
          const hole = new Path();
          hole.moveTo(cx - hw, sezioneCy - hh);
          hole.lineTo(cx + hw, sezioneCy - hh);
          hole.lineTo(cx + hw, sezioneCy + hh);
          hole.lineTo(cx - hw, sezioneCy + hh);
          hole.lineTo(cx - hw, sezioneCy - hh);
          shape.holes.push(hole);
        });
      }
    } else {
      windowPositions.forEach(([wx, wy], i) => {
        const key = `${panelIndex}-${i}`;
        if (finestreAttive.has(key) && altezza >= finestraH) {
          const x = finestraSlotX[i] ?? wx;
          const hw = finestraW / 2;
          const hh = finestraH / 2;
          const hole = new Path();
          hole.moveTo(x - hw, wy - hh);
          hole.lineTo(x + hw, wy - hh);
          hole.lineTo(x + hw, wy + hh);
          hole.lineTo(x - hw, wy + hh);
          hole.lineTo(x - hw, wy - hh);
          shape.holes.push(hole);
        }
      });
      grigliaPositions.forEach(([wx, wy], i) => {
        const key = `${panelIndex}-${i}`;
        if (griglieAttive.has(key) && altezza >= gridSlotH) {
          const x = grigliaSlotX[i] ?? wx;
          const hw = gridSlotW / 2;
          const hh = gridSlotH / 2;
          const hole = new Path();
          hole.moveTo(x - hw, wy - hh);
          hole.lineTo(x + hw, wy - hh);
          hole.lineTo(x + hw, wy + hh);
          hole.lineTo(x - hw, wy + hh);
          hole.lineTo(x - hw, wy - hh);
          shape.holes.push(hole);
        }
      });
    }

    return shape;
  }, [larghezza, altezza, finestreAttive, griglieAttive, panelIndex, windowPositions, grigliaPositions, finestraSlotX, grigliaSlotX, finestraW, finestraH, gridSlotW, gridSlotH, isFullVision, isGlassWindowType, campateSegments, sezioneH, sezioneCy]);

  // Con la sezione parziale il corpo pannello resta del colore porta e la cornice in alluminio
  // fa da bordo al solo vetro: stessa sagoma del pannello, ridotta all'altezza della sezione.
  const frameGeometry = useMemo(() => {
    if (!isSezioneParziale) return null;

    const shape = new Shape();
    shape.moveTo(-larghezza / 2, -sezioneH / 2);
    shape.lineTo( larghezza / 2, -sezioneH / 2);
    shape.lineTo( larghezza / 2,  sezioneH / 2);
    shape.lineTo(-larghezza / 2,  sezioneH / 2);
    shape.lineTo(-larghezza / 2, -sezioneH / 2);

    const BORDO_GP = MM(30);
    const hh = sezioneH / 2 - BORDO_GP;
    campateSegments.forEach(([cx, w]) => {
      const hw = w / 2 - BORDO_GP;
      const hole = new Path();
      hole.moveTo(cx - hw, -hh);
      hole.lineTo(cx + hw, -hh);
      hole.lineTo(cx + hw,  hh);
      hole.lineTo(cx - hw,  hh);
      hole.lineTo(cx - hw, -hh);
      shape.holes.push(hole);
    });

    const geo = new ExtrudeGeometry(shape, { depth: spessore + MM(2), bevelEnabled: false });
    const uvAttr = geo.attributes.uv;
    for (let i = 0; i < uvAttr.count; i++) {
      uvAttr.setXY(i,
        (uvAttr.getX(i) + larghezza / 2) / larghezza,
        (uvAttr.getY(i) + sezioneH / 2) / sezioneH,
      );
    }
    uvAttr.needsUpdate = true;
    return geo;
  }, [isSezioneParziale, larghezza, sezioneH, spessore, campateSegments]);

  const panelGeometry = useMemo(() => {
    const geo = new ExtrudeGeometry(panelShape, { depth: spessore, bevelEnabled: false });
    const uvAttr = geo.attributes.uv;
    const minX = -larghezza / 2;
    const minY = -altezza / 2;
    for (let i = 0; i < uvAttr.count; i++) {
      uvAttr.setXY(i,
        (uvAttr.getX(i) - minX) / larghezza,
        (uvAttr.getY(i) - minY) / altezza,
      );
    }
    uvAttr.needsUpdate = true;
    return geo;
  }, [panelShape, spessore, larghezza, altezza]);

  const backFaceGeometry = useMemo(() => {
    const geo = new ShapeGeometry(panelShape);
    const uvAttr = geo.attributes.uv;
    const minX = -larghezza / 2;
    const minY = -altezza / 2;
    for (let i = 0; i < uvAttr.count; i++) {
      uvAttr.setXY(i,
        (uvAttr.getX(i) - minX) / larghezza,
        (uvAttr.getY(i) - minY) / altezza,
      );
    }
    uvAttr.needsUpdate = true;
    return geo;
  }, [panelShape, larghezza, altezza]);

  const vetroMaterial = vetroSatinato ? VETRO_SATINATO_MATERIAL : VETRO_TRASPARENTE_MATERIAL;

  const showClickZones = (isFinestre && windowSize !== WindowSize.undefined) || (isVentilazione && gridSize !== GridSize.undefined);
  const isGlassActive = isGlassWindowType && finestreAttive.has(`${panelIndex}-0`);
  const slotCount = Math.max(windowPositions.length, grigliaPositions.length);

  return (
    <group>
      {/* Corpo pannello normale – nascosto se superficie areata attiva */}
      {!isPanelSuperficieAttiva && (
        <>
          <mesh geometry={panelGeometry} position={[0, 0, -spessore / 2]}>
            <meshStandardMaterial
              key={(isGlassActive && !isSezioneParziale && corniceFinestratura ? corniceFinestratura : texPannello)?.uuid ?? 'front'}
              map={isGlassActive && !isSezioneParziale && corniceFinestratura ? corniceFinestratura : texPannello}
              roughness={1}
              metalness={0}
            />
          </mesh>

          {/* Retro pannello – lato interno (visibile dall'interno del garage) */}
          <mesh geometry={backFaceGeometry} position={[0, 0, -spessore / 2 - MM(0.5)]}>
            <meshStandardMaterial
              key={texPannelloInterno?.uuid ?? 'back'}
              map={texPannelloInterno}
              roughness={1}
              metalness={0}
              side={BackSide}
            />
          </mesh>

          {isGlassActive && frameGeometry && (
            <mesh geometry={frameGeometry} position={[0, sezioneCy, -spessore / 2 - MM(1)]}>
              <meshStandardMaterial
                key={corniceFinestratura?.uuid ?? 'cornice'}
                map={corniceFinestratura}
                roughness={1}
                metalness={0}
              />
            </mesh>
          )}

          {isGlassActive && campateSegments.map(([cx, w], col) => {
            const gw = w - MM(60);
            return (
              <mesh
                key={col}
                position={[cx, sezioneCy, 0]}
                onClick={(e) => { if (isDoorClosed && isFinestre) { e.stopPropagation(); onFinestraClick(panelIndex, 0); } }}
                onPointerOver={(e) => { if (isDoorClosed && isFinestre) { e.stopPropagation(); document.body.style.cursor = "pointer"; } }}
                onPointerOut={() => { document.body.style.cursor = "default"; }}
              >
                <boxGeometry args={[gw, sezioneH - MM(60), spessore + MM(2)]} />
                <meshStandardMaterial {...vetroMaterial} transparent side={DoubleSide} />
                <Edges color={C_VETRO_BORDO} />
              </mesh>
            );
          })}

          {campateSegments.slice(1).map(([cxDestra, wDestra], i) => {
            const [cxSinistra, wSinistra] = campateSegments[i];
            const x = ((cxSinistra + wSinistra / 2) + (cxDestra - wDestra / 2)) / 2;
            const texColonna = texturaTile(colorePannello, ALTEZZA_RAIL_M, altezza, x - ALTEZZA_RAIL_M / 2 + larghezza / 2, baseY);
            return (
              <mesh key={i} position={[x, 0, RILIEVO_RAIL_M / 2]}>
                <boxGeometry args={[ALTEZZA_RAIL_M, altezza, spessore + RILIEVO_RAIL_M]} />
                <meshStandardMaterial key={texColonna?.uuid ?? 'pietra-col'} map={texColonna} roughness={1} metalness={0} />
                <Edges color={hoverColor} />
              </mesh>
            );
          })}

          {!isFullVision && !isGlassActive && wideSectionSegments.map(([cx, w], idx) => {
            const sectionH = altezza / 2 - MM(25);
            const sezioneX   = cx - w / 2 + larghezza / 2;
            const texSezSopra = texturaTile(colorePannello, w, sectionH, sezioneX, baseY + altezza - MM(25) - sectionH);
            const texSezSotto = texturaTile(colorePannello, w, sectionH, sezioneX, baseY);
            return (
              <React.Fragment key={idx}>
                <mesh position={[cx, altezza / 2 - MM(25) - sectionH / 2, RILIEVO_RAIL_M / 2]}>
                  <boxGeometry args={[w, sectionH, spessore + RILIEVO_RAIL_M]} />
                  <meshStandardMaterial key={texSezSopra?.uuid ?? 'wide-top'} map={texSezSopra} roughness={1} metalness={0} />
                  <Edges color={hoverColor} />
                </mesh>
                <mesh position={[cx, -altezza / 2 + sectionH / 2, RILIEVO_RAIL_M / 2]}>
                  <boxGeometry args={[w, sectionH, spessore + RILIEVO_RAIL_M]} />
                  <meshStandardMaterial key={texSezSotto?.uuid ?? 'wide-bot'} map={texSezSotto} roughness={1} metalness={0} />
                  <Edges color={hoverColor} />
                </mesh>
              </React.Fragment>
            );
          })}

          {isFullVision && (
            <mesh position={[0, 0, MM(2)]}>
              <boxGeometry args={[larghezza - 2 * TELAIO_FV, altezza - 2 * TELAIO_FV, MM(8)]} />
              <meshStandardMaterial {...(vetroSatinato ? VETRO_SATINATO_MATERIAL : { color: C_VETRO, opacity: 0.55 })} transparent />
              <Edges color={C_VETRO_BORDO} />
            </mesh>
          )}
        </>
      )}

      {/* Rail superiore e inferiore – sempre visibili */}
      <mesh position={[0, altezza / 2 - ALTEZZA_RAIL_M / 2, RILIEVO_RAIL_M / 2]}>
        <boxGeometry args={[larghezza, ALTEZZA_RAIL_M, spessore + RILIEVO_RAIL_M]} />
        <meshStandardMaterial key={texRailTop?.uuid ?? 'rail-top'} map={texRailTop} roughness={1} metalness={0} />
        <Edges color={hoverColor} />
      </mesh>
      <mesh position={[0, -altezza / 2 + ALTEZZA_RAIL_M / 2, RILIEVO_RAIL_M / 2]}>
        <boxGeometry args={[larghezza, ALTEZZA_RAIL_M, spessore + RILIEVO_RAIL_M]} />
        <meshStandardMaterial key={texRailBot?.uuid ?? 'rail-bot'} map={texRailBot} roughness={1} metalness={0} />
        <Edges color={hoverColor} />
      </mesh>

      {/* Superficie areata attiva: pannello alluminio forato */}
      {isPanelSuperficieAttiva && (
        <PannelloSuperficieAerata
          larghezza={larghezza}
          altezza={altezza}
          spessore={spessore}
          tipo={gridSize === GridSize.superificie_ft ? "ft" : "fq"}
          onRemove={isVentilazione ? () => onFinestraClick(panelIndex, 0) : () => {}}
          isDoorClosed={isVentilazione ? isDoorClosed : false}
          colore={grigliaColor}
        />
      )}

      {/* Zona cliccabile sull'intero pannello per superficie areata non attiva e senza finestre */}
      {isSuperficieAreata && !isPanelSuperficieAttiva && !panelHasFinestre && altezza >= MM(200) && (
        <ZonaCliccabile
          position={[0, 0, 0]}
          slotW={larghezza}
          slotH={altezza}
          spessore={spessore}
          onClick={() => onFinestraClick(panelIndex, 0)}
          isDoorClosed={isDoorClosed}
          hoverColor={C_AREA_SELEZIONABILE}
        />
      )}

      {/* Glasspanel zona cliccabile (solo step finestratura) */}
      {isGlasspanel && !isGlassActive && !showPortina && !isFullVision && altezza >= (glasspanelAltezza ?? MM(300)) && !glasspanelLimitReached && (
        <ZonaCliccabile
          position={[0, 0, RILIEVO_RAIL_M]}
          slotW={larghezza}
          slotH={altezza}
          spessore={spessore}
          onClick={() => onFinestraClick(panelIndex, 0)}
          isDoorClosed={isDoorClosed}
          hoverColor={C_AREA_SELEZIONABILE}
        />
      )}

      {!isGlasspanel && !isGlassActive && !isFullVision && !isSuperficieAreata && slotCount > 0 && Array.from({ length: slotCount }, (_, i) => {
        const key = `${panelIndex}-${i}`;
        const hasFinestra = finestreAttive.has(key);
        const hasGriglia  = griglieAttive.has(key);

        // X ricentrata sul vano della portina quando serve; null = slot a cavallo del telaio.
        const wSlotX = finestraSlotX[i];
        const gSlotX = grigliaSlotX[i];

        const wPosBase = windowPositions[i] as [number, number, number] | undefined;
        const gPosBase = grigliaPositions[i] as [number, number, number] | undefined;
        const wPos = wPosBase ? ([wSlotX ?? wPosBase[0], wPosBase[1], wPosBase[2]] as [number, number, number]) : undefined;
        const gPos = gPosBase ? ([gSlotX ?? gPosBase[0], gPosBase[1], gPosBase[2]] as [number, number, number]) : undefined;

        const clickPos    = isVentilazione ? gPos : wPos;
        const clickSlotW  = isVentilazione ? gridSlotW : finestraW;
        const clickSlotH  = isVentilazione ? gridSlotH : finestraH;
        const bloccataDaPortina  = (isVentilazione ? gSlotX : wSlotX) === null;
        const bloccataDaManiglia = !!clickPos && slotOverlapsManiglia(clickPos[0], panelWorldY, clickSlotW, clickSlotH, tipoManiglia, manigliaLockY, manigliaSquareY);
        const bloccata           = bloccataDaPortina || bloccataDaManiglia;

        return (
          <group key={i}>
            {showClickZones && !hasFinestra && !hasGriglia && !bloccata && !!clickPos && altezza >= clickSlotH && (
              <ZonaCliccabile
                position={clickPos}
                slotW={clickSlotW}
                slotH={clickSlotH}
                spessore={spessore}
                onClick={() => onFinestraClick(panelIndex, i)}
                isDoorClosed={isDoorClosed}
                hoverColor={C_AREA_SELEZIONABILE}
              />
            )}
            {hasFinestra && !!wPos && altezza >= finestraH && !isGlassWindowType && (
              <Finestra
                position={wPos}
                spessore={spessore}
                finestraLarghezza={finestraW}
                finestraAltezza={finestraH}
                cornice={corniceFinestratura}
                onRemove={isFinestre ? () => onFinestraClick(panelIndex, i) : () => {}}
                isDoorClosed={isFinestre ? isDoorClosed : false}
              />
            )}
            {hasGriglia && !!gPos && altezza >= gridSlotH && !isSuperficieAreataType && (
              <Griglia
                position={gPos}
                larghezza={gridSlotW}
                altezza={gridSlotH}
                spessore={spessore}
                onRemove={isVentilazione ? () => onFinestraClick(panelIndex, i) : () => {}}
                isDoorClosed={isVentilazione ? isDoorClosed : false}
                colore={grigliaColor}
              />
            )}
          </group>
        );
      })}
    </group>
  );
};
